import React, { useState, useEffect } from 'react';
import { Typography, Button, Box, IconButton, Tooltip as MuiTooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import awsLogo from '../assets/Website/AWS.svg';
import googleLogo from '../assets/cloud-providers/google-cloud-icon.svg';
import azureLogo from '../assets/Website/Microsoft_Azure.svg';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate, useLocation } from 'react-router-dom';
import { TabIndex } from '../navigation';
import { useApi } from '../contexts/ApiContext';
import { useWithTokenRefresh } from '../utils/api';
// StatsData type removed – V2 API uses a different shape
import { useAuth, useSelectedWorkspace, useWorkspaces } from '../contexts/AuthContext';
import { useMFA } from '../contexts/MFAContext';
import { ApiTypes } from '@flashbacktech/flashbackclient/api';
import multicloudImg from '../assets/landing-page/multicloud.png';
import dataIcon from '../assets/onboard/data.svg';
import thunderIcon from '../assets/onboard/thunder.svg';
import docsIcon from '../assets/sidebar-icons/docs-link.svg';
import eyeSvg from '../assets/details-bucket/eye.svg';
import deleteSvg from '../assets/details-bucket/delete.svg';
import settingsSvg from '../assets/details-bucket/settings.svg';
import { toastError } from '../utils/toast';

// Types
interface BucketData {
  name: string;
  uploadedBytes: string;
  downloadedBytes: string;
  status: string;
  id?: string;
  storageType?: ApiTypes.StorageType;
}

// Type pour les données du graphique camembert
interface TransferDataItem {
  name: string;
  value: number;
  valueInGB?: number;
  color: string;
  id?: string;
  rawValue?: number;
  uploadedBytes?: number;
  downloadedBytes?: number;
}

interface RecommendationData {
  title: string;
  benefit: string;
  category1: string;
  category2: string;
}

interface ActivityItem {
  action: string;
  timestamp: string;
  description: string;
}

const OverviewMockup: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Helper function to navigate to a tab using custom events
  const navigateToTab = (tabIndex: number, path: string) => {
    const rootEl = document.getElementById('root');
    if (rootEl) {
      rootEl.dispatchEvent(new CustomEvent('navigateToTab', { detail: { tabIndex } }));
    }
    window.dispatchEvent(new CustomEvent('navigateToTab', { detail: { tabIndex } }));
    navigate(path);
  };
  const { apiClient } = useApi();
  const withTokenRefresh = useWithTokenRefresh();
  const { authState } = useAuth();
  const [repos, setRepos] = useState<any[]>([]);
  // États pour gérer l'affichage de la carte de documentation
  const [showOnboardingCard, setShowOnboardingCard] = useState(true);
  const [isCardCollapsed, setIsCardCollapsed] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [buckets, setBuckets] = useState<BucketData[]>([]);
  const [unitStats, setUnitStats] = useState<Record<string, any>>({});
  // Use centralized workspace hooks
  const globalWorkspaces = useWorkspaces();
  const globalSelectedWorkspaceId = useSelectedWorkspace();
  const { setSelectedWorkspace } = useAuth();
  
  // Local state for workspace selection (can be overridden locally)
  const [localWorkspaces, setLocalWorkspaces] = useState<{ id: string; name: string }[]>([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>(globalSelectedWorkspaceId);
  
  // Use global workspaces if local not loaded
  const workspaces = localWorkspaces.length > 0 ? localWorkspaces : globalWorkspaces;
  const setWorkspaces = setLocalWorkspaces;
  const createWorkspace = async () => {
    try {
      const name = window.prompt('Workspace name');
      if (!name || !name.trim()) return;
      const res = await withTokenRefresh(() => apiClient.createWorkspace({ name: name.trim() }));
      if (res.success && res.workspace?.id) {
        const w = { id: res.workspace.id as string, name: res.workspace.name as string };
        setWorkspaces((prev) => [...prev, w]);
        setSelectedWorkspaceId(w.id);
        try {
          localStorage.setItem('selectedWorkspaceId', w.id);
        } catch (e) {
          /* ignore */
        }
      } else {
        toastError('Failed to create workspace');
      }
    } catch (e) {
      toastError('Error creating workspace');
    }
  };

  // Données pour le graphique camembert "Total Data Transfer"
  const [transferData, setTransferData] = useState<TransferDataItem[]>([
    // Initialisation avec des valeurs à zéro au lieu de données factices
    { name: 'No data', value: 0, valueInGB: 0, color: '#d4d4d4' },
  ]);

  // Fonction pour calculer les 5 buckets avec le plus grand volume de transfert
  const calculateTopBuckets = () => {
    if (!buckets.length || Object.keys(unitStats).length === 0) {
      return;
    }

    // Créer un tableau avec les données de transfert pour chaque bucket
    const bucketsTransferData = buckets
      .filter((bucket) => {
        const hasStats = bucket.id && unitStats[bucket.id];
        return hasStats;
      })
      .map((bucket) => {
        const stats = unitStats[bucket.id!];

        // Récupérer les valeurs d'upload et download
        const uploadedBytes = stats.uploadedBytes;
        const downloadedBytes = stats.downloadedBytes;

        // Convertir en nombres
        const uploadNum = Number(uploadedBytes);
        const downloadNum = Number(downloadedBytes);

        // Additionner pour avoir le total
        const totalTransfer = uploadNum + downloadNum;

        return {
          id: bucket.id,
          name: bucket.name,
          value: totalTransfer, // Valeur pour le camembert
          uploadedBytes: uploadNum,
          downloadedBytes: downloadNum,
        };
      })
      // Trier par volume de transfert décroissant
      .sort((a, b) => b.value - a.value)
      // Prendre les 5 premiers
      .slice(0, 5);

    // Assigner des couleurs fixes (charte graphique du site)
    // Bleu primaire, Orange, Turquoise, Violet, Vert
    const colors = ['#617BFF', '#F79009', '#3DD9D6', '#7A5AF8', '#12B76A'];
    const finalData = bucketsTransferData.map((item, index) => ({
      ...item,
      color: colors[index % colors.length],
    }));

    if (finalData.length > 0) {
      setTransferData(finalData);
    } else { /* Empty */ }
  };

  // V2 org-level stats don't include per-bucket breakdown, so use unitStats for the pie chart
  useEffect(() => {
    calculateTopBuckets();
  }, [unitStats, buckets]);

  // Use centralized workspace data from AuthContext
  const { globalData } = useAuth();

  // Update local state when global data changes - use ref to track previous value
  const prevWorkspaceRef = React.useRef<string>('');

  useEffect(() => {
    setWorkspaces(globalData.workspaces);

    // Always sync with globalData.selectedWorkspaceId when it changes
    if (globalData.selectedWorkspaceId && globalData.selectedWorkspaceId !== selectedWorkspaceId) {
      console.log('[OverviewMockup] Syncing workspace from globalData:', globalData.selectedWorkspaceId);
      setSelectedWorkspaceId(globalData.selectedWorkspaceId);
    }
  }, [globalData.workspaces, globalData.selectedWorkspaceId, globalData.isReady]);

  // Debug: Log when repos state changes
  useEffect(() => { /* Empty */ }, [repos]);

  // Sync with global workspace selection
  useEffect(() => {
    if (globalSelectedWorkspaceId && globalSelectedWorkspaceId !== selectedWorkspaceId) {
      console.log('[OverviewMockup] Syncing workspace from global:', globalSelectedWorkspaceId);
      setSelectedWorkspaceId(globalSelectedWorkspaceId);
    }
  }, [globalSelectedWorkspaceId]);

  // Listen for workspace change events from Sidebar
  useEffect(() => {
    const handleWorkspaceChanged = (e: CustomEvent) => {
      const newWorkspaceId = e.detail?.workspaceId;
      console.log('[OverviewMockup] workspaceChanged event received:', newWorkspaceId);
      if (newWorkspaceId && newWorkspaceId !== selectedWorkspaceId) {
        setSelectedWorkspaceId(newWorkspaceId);
      }
    };
    window.addEventListener('workspaceChanged', handleWorkspaceChanged as any);
    return () => {
      window.removeEventListener('workspaceChanged', handleWorkspaceChanged as any);
    };
  }, [selectedWorkspaceId]);

  // Event listener to close menus when clicking outside (for three-dots dropdowns)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        !target.closest('.dropdown-menu') &&
        !target.closest('button[aria-label="More options"]')
      ) {
        document.querySelectorAll('.dropdown-menu').forEach((menu) => {
          menu.classList.add('hidden');
        });
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Fonction pour convertir les octets en format lisible
  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Fonction pour formater les bytes en format compact (max 4 caractères significatifs)
  // Auto-switch to TB if >= 1000 GB, format US avec point décimal
  const formatBytesCompact = (bytes: number): string => {
    if (bytes === 0) return '0 GB';
    const k = 1024;
    const gb = bytes / (k * k * k);
    const tb = gb / k;
    
    // Use TB if >= 1000 GB
    if (gb >= 1000) {
      // Format TB with max 4 significant characters
      if (tb >= 100) return `${Math.round(tb)} TB`;
      if (tb >= 10) return `${tb.toFixed(1).slice(0, 4)} TB`;
      return `${tb.toFixed(2).slice(0, 4)} TB`;
    }
    
    // Format GB with max 4 significant characters
    if (gb >= 100) return `${Math.round(gb)} GB`;
    if (gb >= 10) return `${gb.toFixed(1).slice(0, 4)} GB`;
    if (gb >= 1) return `${gb.toFixed(2).slice(0, 4)} GB`;
    return `${gb.toFixed(2).slice(0, 4)} GB`;
  };

  // Fetch per-bucket stats via V2 storage gateway breakdown
  const fetchUnitStats = async (unitIds: string[]) => {
    if (!unitIds.length) return;

    try {
      const newStats: Record<string, any> = {};

      const breakdownResponse = await withTokenRefresh(() =>
        apiClient.getStorageGatewayBreakdown('buckets', { scope: 'org', window: '30d' } as any)
      ) as any;

      if (breakdownResponse?.entities && Array.isArray(breakdownResponse.entities)) {
        breakdownResponse.entities.forEach((entity: any) => {
          if (entity.entityId && unitIds.includes(entity.entityId)) {
            const t = entity.totals || {};
            newStats[entity.entityId] = {
              calls: t.requests || 0,
              storageChange: t.sizeChange || 0,
              downloadedBytes: t.dwlBytes || 0,
              uploadedBytes: t.uplBytes || 0,
              isOnline: false,
              lastSeen: 0,
            };
          }
        });
      }

      unitIds.forEach((id) => {
        if (!newStats[id]) {
          newStats[id] = {
            calls: 0,
            storageChange: 0,
            downloadedBytes: 0,
            uploadedBytes: 0,
            isOnline: false,
            lastSeen: 0,
          };
        }
      });

      setUnitStats(newStats);
    } catch (error) { /* Ignore errors */ }
  };

  // Fonction pour récupérer les buckets (unités de stockage) et les repos
  // Returns { bucketIds, bucketData } for use in fetchDailyStats
  const loadStorageUnits = async (workspaceId?: string): Promise<{ bucketIds: string[]; bucketData: BucketData[] }> => {
    try {
      // Fetch repos directly (like StorageRepos.tsx does) to avoid apiClient bugs
      const token = authState?.token;
      const baseUrl = process.env.REACT_APP_API_URL || 'https://backend.flashback.tech';

      const [unitsResponse, reposResponse] = await Promise.all([
        withTokenRefresh(apiClient.getStorageBuckets.bind(apiClient)),
        fetch(`${baseUrl}/repo`, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: token ? `Bearer ${token}` : '',
          },
        }).then((res) => (res.ok ? res.json() : { repos: [] })),
      ]);

      // Process storage units (buckets)
      if (unitsResponse?.buckets) {
        // Optional filter by workspace (support multiple shapes)
        const filtered = (unitsResponse.buckets as any[]).filter((unit: any) => {
          if (!workspaceId || workspaceId === 'ALL') return true;
          const wid =
            unit.workspaceId ||
            unit.workspace_id ||
            (unit.workspace && (unit.workspace.id || unit.workspace)) ||
            unit.orgId ||
            unit.org_id;
          return wid === workspaceId;
        });

        // Transformer les données de l'API en format BucketData
        const bucketData: BucketData[] = filtered.map((unit: any) => ({
          id: unit.id,
          name: unit.name,
          uploadedBytes: '0 B', // Sera mis à jour avec les stats réelles
          downloadedBytes: '0 B', // Sera mis à jour avec les stats réelles
          status: unit.status || 'Online',
          storageType: unit.storageType, // Include storage type for logo retrieval
        }));

        setBuckets(bucketData);

        // Récupérer les statistiques une fois les unités chargées
        const unitIds = filtered.map((unit: any) => unit.id).filter(Boolean);

        await fetchUnitStats(unitIds);
        
        // Process repos - filter by workspace like buckets (using StorageRepos pattern)
        if (reposResponse?.repos && Array.isArray(reposResponse.repos)) {
          const allRepos = reposResponse.repos;
          const filteredRepos =
            !workspaceId || workspaceId === 'ALL'
              ? allRepos
              : allRepos.filter((repo: any) => {
                  const wid =
                    repo.workspaceId || repo.workspace_id || repo.workspace?.id || repo.workspace;
                  const match = String(wid) === String(workspaceId);
                  return match;
                });

          setRepos(filteredRepos);
        } else {
          setRepos([]);
        }
        
        return { bucketIds: unitIds, bucketData }; // Return bucket IDs and data for fetchDailyStats
      } else {
        setBuckets([]);
        setRepos([]);
        return { bucketIds: [], bucketData: [] };
      }
    } catch (error) {
      // En cas d'erreur, réinitialiser les deux états
      setBuckets([]);
      setRepos([]);
      return { bucketIds: [], bucketData: [] };
    }
  };

  // Charger les buckets au chargement du composant et à chaque navigation vers cette page
  // Use ref to track if we're currently loading to prevent duplicate loads
  const isLoadingRef = React.useRef(false);

  useEffect(() => {
    let isMounted = true;

    console.log('[OverviewMockup] Main useEffect triggered, selectedWorkspaceId:', selectedWorkspaceId);

    const initializeData = async () => {
      console.log('[OverviewMockup] initializeData called for workspace:', selectedWorkspaceId);
      
      // Reset transfer data when workspace changes to avoid showing stale data
      setTransferData([{ name: 'No data', value: 0, valueInGB: 0, color: '#d4d4d4' }]);
      
      // Reset loading ref to allow new load
      isLoadingRef.current = false;

      isLoadingRef.current = true;
      if (isMounted) setIsLoading(true);

      // Wait for global data to be ready before fetching data
      if (!globalData.isReady) {
        console.log('[OverviewMockup] globalData not ready, skipping load');
        if (isMounted) setIsLoading(false);
        isLoadingRef.current = false;
        return;
      }

      try {
        // Charger les buckets et les statistiques
        console.log('[OverviewMockup] Loading storage units for workspace:', selectedWorkspaceId);
        const { bucketIds, bucketData } = await loadStorageUnits(selectedWorkspaceId);
        console.log('[OverviewMockup] Loaded', bucketIds.length, 'buckets, fetching daily stats...');
        await fetchDailyStats(bucketIds, bucketData);
      } catch (error) { 
        console.error('[OverviewMockup] Error loading data:', error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
          setInitialLoadComplete(true);
        }
        isLoadingRef.current = false;
      }
    };

    // Initialiser les données à chaque fois que la page est visitée ou workspace change
    initializeData();

    // Configurer une actualisation périodique des statistiques (toutes les 5 minutes)
    const statsRefreshInterval = setInterval(() => {
      const currentUnitIds = buckets.map((bucket) => bucket.id || '').filter((id) => id !== '');
      if (currentUnitIds.length > 0) {
        fetchUnitStats(currentUnitIds);
        fetchDailyStats(currentUnitIds, buckets);
      }
    }, 300000); // 5 minutes

    return () => {
      isMounted = false;
      clearInterval(statsRefreshInterval);
    };
  }, [location.pathname, selectedWorkspaceId, globalData.isReady]); // reload when path, workspace or global data ready changes

  // Recharger les données 5 secondes après le chargement initial
  useEffect(() => {
    if (!initialLoadComplete) return;

    const reloadTimer = setTimeout(async () => {
      const { bucketIds, bucketData } = await loadStorageUnits(selectedWorkspaceId);
      fetchDailyStats(bucketIds, bucketData);
    }, 3000);

    return () => {
      clearTimeout(reloadTimer);
    };
  }, [initialLoadComplete]); // Trigger only when initial load completes

  // Données pour les graphiques d'activité de transfert basées sur les données des 30 derniers jours
  const [downloadActivityData, setDownloadActivityData] = useState<
    { date: string; download: number }[]
  >([]);

  const [uploadActivityData, setUploadActivityData] = useState<{ date: string; upload: number }[]>(
    []
  );

  // État pour gérer quelles courbes sont visibles dans le graphique Transfer Activity
  const [visibleTransfers, setVisibleTransfers] = useState<{ download: boolean; upload: boolean }>({
    download: true,
    upload: true,
  });

  // Helper to set empty chart data for 31 days (30 past days + today)
  const setEmptyChartData = () => {
    const emptyData = Array.from({ length: 31 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (30 - i));
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      return `${month}/${day}`;
    });
    setDownloadActivityData(emptyData.map((date) => ({ date, download: 0 })));
    setUploadActivityData(emptyData.map((date) => ({ date, upload: 0 })));
  };

  // Fetch 30-day storage gateway stats via V2 API
  const fetchDailyStats = async (_passedBucketIds?: string[], _passedBucketData?: BucketData[]) => {
    try {
      console.log('[OverviewMockup] Fetching V2 storage gateway stats (30d)');

      const response = await withTokenRefresh(() =>
        apiClient.getStorageGatewayStats({ scope: 'org', window: '30d' } as any)
      ) as any;

      console.log('[OverviewMockup] V2 stats response:', response);

      if (response.success && response.series && response.series.length > 0) {
        processStatsForCharts(response.series);
      } else {
        console.log('[OverviewMockup] No V2 series data, using empty chart');
        setEmptyChartData();
      }
    } catch (error) {
      console.error('[OverviewMockup] Error fetching V2 stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Process V2 time-series data for the area charts
  const processStatsForCharts = (series: Array<{ ts: number; uplBytes: number; dwlBytes: number; sizeChange: number; latencyMs: number; requests: number }>) => {
    console.log('[OverviewMockup] processStatsForCharts (V2) called with', series.length, 'points');

    // V2 returns data bucketed by 6h intervals for a 30d window.
    // Aggregate into daily buckets keyed by midnight-UTC timestamp.
    const downloadByDay = new Map<number, number>();
    const uploadByDay = new Map<number, number>();

    series.forEach((point) => {
      const d = new Date(point.ts * 1000);
      const dayTs = Math.floor(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / 1000);
      const dl = Number(point.dwlBytes || 0);
      const ul = Number(point.uplBytes || 0);
      downloadByDay.set(dayTs, (downloadByDay.get(dayTs) || 0) + dl);
      uploadByDay.set(dayTs, (uploadByDay.get(dayTs) || 0) + ul);
    });

    // Build a complete 31-day array (30 past days + today)
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    const allDays: { date: Date; timestamp: number }[] = [];
    for (let i = 0; i <= 30; i++) {
      const date = new Date(thirtyDaysAgo.getTime() + i * 24 * 60 * 60 * 1000);
      const normalizedDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      const timestamp = Math.floor(normalizedDate.getTime() / 1000);
      allDays.push({ date: normalizedDate, timestamp });
    }

    const downloadChartData = allDays.map(({ date, timestamp }) => {
      const bytes = downloadByDay.get(timestamp) || 0;
      const downloadGB = bytes / (1024 * 1024 * 1024);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      return { date: `${month}/${day}`, download: parseFloat(downloadGB.toFixed(2)) };
    });

    const uploadChartData = allDays.map(({ date, timestamp }) => {
      const bytes = uploadByDay.get(timestamp) || 0;
      const uploadGB = bytes / (1024 * 1024 * 1024);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      return { date: `${month}/${day}`, upload: parseFloat(uploadGB.toFixed(2)) };
    });

    setDownloadActivityData(downloadChartData);
    setUploadActivityData(uploadChartData);
  };

  // Load daily statistics data when component mounts - REMOVED duplicate (handled in main useEffect)

  // Données pour les recommandations IA (vides par défaut)
  const [recommendations] = useState<RecommendationData[]>([]);

  // Données d'activité récente (vides par défaut)
  const [recentActivity] = useState<ActivityItem[]>([]);

  // Custom tooltip for pie chart
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      // Afficher les valeurs brutes pour le débogage
      return (
        <div className="bg-white p-2 border border-gray-200 shadow-sm text-sm">
          <p className="font-semibold">{data.name}</p>
          <p>{`Total: ${formatBytes(data.value)}`}</p>
          <p>{`↑ Upload: ${formatBytes(data.uploadedBytes)}`}</p>
          <p>{`↓ Download: ${formatBytes(data.downloadedBytes)}`}</p>
        </div>
      );
    }
    return null;
  };

  // Le message de bienvenue est maintenant géré dans App.tsx

  // Calculate the number of buckets and repos
  const bucketCount = buckets.length;
  const repoCount = repos.length;

  // Extract name from user data and use first part as firstName
  const userName = authState?.user?.name || 'User';
  const firstName = userName.split(' ')[0];

  // Onboarding checklist derived state (profile/2FA unknown => treated as incomplete for now)
  const hasBuckets = buckets.length > 0;
  const hasRepos = repos.length > 0;
  const hasAnyApiKey = Array.isArray(repos)
    ? repos.some((r: any) => Array.isArray(r.apiKeys) && r.apiKeys.length > 0)
    : false;
  const hasAnyTransfer = Object.values(unitStats || {}).some((s: any) => {
    const up = Number(s?.uploadedBytes || 0);
    const down = Number(s?.downloadedBytes || 0);
    return up + down > 0;
  });

  // Total transfer value for the pie card to detect empty state
  const totalTransferValue = transferData.reduce((sum, item) => sum + (Number(item.value) || 0), 0);

  type OnboardingStepId = 'profile' | '2fa' | 'bucket' | 'repo' | 'apikey' | 'upload';

  interface OnboardingStep {
    id: OnboardingStepId;
    completed: boolean;
  }

  // Check if 2FA is enabled
  const { mfaState } = useMFA();
  const is2FAEnabled = mfaState?.status?.isEnabled || false;

  // Check if onboarding is already marked as completed in localStorage
  const isOnboardingCompletedInCache = (() => {
    try {
      return localStorage.getItem('onboardingCompleted') === 'true';
    } catch {
      return false;
    }
  })();

  const steps: OnboardingStep[] = [
    { id: 'profile', completed: true }, // Align with sidebar default blue-check; TODO: wire real flag when available
    { id: '2fa', completed: is2FAEnabled }, // 2FA is completed if enabled
    { id: 'bucket', completed: hasBuckets },
    { id: 'repo', completed: hasRepos },
    { id: 'apikey', completed: hasAnyApiKey },
    { id: 'upload', completed: hasAnyTransfer },
  ];

  const firstIncomplete = steps.find((s) => !s.completed);
  const allQuestsCompleted = steps.every((s) => s.completed);

  // Save to localStorage when all quests are completed
  React.useEffect(() => {
    if (allQuestsCompleted && !isOnboardingCompletedInCache) {
      try {
        localStorage.setItem('onboardingCompleted', 'true');
      } catch (error) { /* Ignore errors */ }
    }
  }, [allQuestsCompleted, isOnboardingCompletedInCache]);

  const getCtaForStep = (id: OnboardingStepId) => {
    switch (id) {
      case 'profile':
        return {
          label: 'Complete profile',
          action: () => navigate('/profile'),
        };
      case '2fa':
        return {
          label: 'Set up 2FA',
          action: () => {
            try {
              localStorage.setItem('triggerProfileSecurity', '1');
            } catch (e) {
              // eslint-disable-next-line no-console
            }
            // Trigger a custom event to notify Sidebar to open ProfileModal
            window.dispatchEvent(new CustomEvent('openProfileSecurity'));
          },
        };
      case 'bucket':
        return {
          label: '+ Add',
          action: () => { localStorage.setItem('triggerAddBucket', '1'); navigateToTab(TabIndex.StorageUnits, '/cloud-storage/buckets'); },
        };
      case 'repo':
        return {
          label: '+ Add repository',
          action: () => navigate('/repositories/inventory/add'),
        };
      case 'apikey':
        return {
          label: 'Generate API key',
          action: () => navigate('/repositories/inventory'),
        };
      case 'upload':
        return {
          label: 'Upload a file',
          action: () => navigate('/cloud-storage/buckets'),
        };
      default:
        return {
          label: 'Learn more',
          action: () =>
            window.open('https://docs.flashback.tech/', '_blank', 'noopener,noreferrer'),
        };
    }
  };
  // Hide only for the current session (no persistence); card will reappear on reload

  const dismissOnboarding = () => {
    setShowOnboardingCard(false);
    // Ensure any legacy flag is cleared so it won't persist hiding across reloads
    try {
      sessionStorage.removeItem('hideOverviewOnboardingPromo');
    } catch (e) {
      // eslint-disable-next-line no-console
    }
  };

  // Global loader during initial data fetch
  if (isLoading && !initialLoadComplete) {
    return (
      <div
        className="flex flex-col items-center justify-center"
        style={{ minHeight: 'calc(100vh - 200px)' }}
      >
        <div
          className="animate-spin rounded-full h-16 w-16 border-b-2 mb-4"
          style={{ borderColor: '#617BFF' }}
        ></div>
        <p className="text-gray-600 text-lg font-medium">Loading overview...</p>
        <p className="text-gray-500 text-sm mt-2">Fetching data and statistics</p>
        <p className="text-gray-400 text-xs mt-3 max-w-md text-center">
          First load may take a few moments. Next visits will be instant thanks to caching.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
      {/* Header */}
      <div style={{ padding: '24px 40px 24px 40px' }}>
        <Typography
          style={{
            fontSize: '32px',
            fontWeight: 700,
            color: '#1F2937',
            marginBottom: '8px',
            fontFamily: 'Inter',
            letterSpacing: '-0.02em',
          }}
        >
          Cloud Storage
        </Typography>
        <Typography
          style={{
            fontSize: '16px',
            fontWeight: 400,
            color: '#6B7280',
            fontFamily: 'Inter',
            lineHeight: '24px',
          }}
        >
          Manage your cloud storage buckets and repositories
        </Typography>
      </div>

      {/* Content */}
      <div className="flex-1 px-10 pb-6 overflow-y-auto">

      {/* Overview Sub-section Header with buttons */}
      <Box sx={{ ml: '8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828', mb: 0.5 }}>
              Overview
            </Typography>
            <Typography sx={{ fontSize: '14px', color: '#667085' }}>
              Welcome back, {firstName}. You currently have {bucketCount} buckets and {repoCount} repos active.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Reload */}
            <MuiTooltip title="Reload">
              <IconButton
                size="small"
                onClick={async () => {
                  setTransferData([{ name: 'No data', value: 0, valueInGB: 0, color: '#d4d4d4' }]);
                  setIsLoading(true);
                  try {
                    const { bucketIds, bucketData } = await loadStorageUnits(selectedWorkspaceId);
                    await fetchDailyStats(bucketIds, bucketData);
                  } catch (e) { /* ignore */ } finally {
                    setIsLoading(false);
                  }
                }}
                sx={{ width: 36, height: 36, border: '1px solid #D0D5DD', borderRadius: '8px', color: '#667085', '&:hover': { bgcolor: '#F9FAFB', borderColor: '#98A2B3' } }}
              >
                <RefreshIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </MuiTooltip>
            {/* Add bucket */}
            <MuiTooltip title="Add">
              <IconButton
                size="small"
                onClick={() => { localStorage.setItem('triggerAddBucket', '1'); navigateToTab(TabIndex.StorageUnits, '/cloud-storage/buckets'); }}
                sx={{ width: 36, height: 36, bgcolor: '#617BFF', borderRadius: '8px', color: 'white', '&:hover': { bgcolor: '#4A67E3' } }}
              >
                <AddIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </MuiTooltip>
          </Box>
        </Box>
      </Box>

      {/* 4 KPI Stats Cards - Same style as AI Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 ml-2">
        {/* Number of Buckets Card */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div style={{ width: 40, height: 40, borderRadius: 8, backgroundColor: '#F0F2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
              </svg>
            </div>
            <Typography sx={{ fontSize: '14px', color: '#717680', fontWeight: 500 }}>Buckets</Typography>
          </div>
          <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#181D27' }}>{bucketCount}</Typography>
        </div>

        {/* Upload (30j sum) Card */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div style={{ width: 40, height: 40, borderRadius: 8, backgroundColor: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
            </div>
            <Typography sx={{ fontSize: '14px', color: '#717680', fontWeight: 500 }}>Upload (30d)</Typography>
          </div>
          <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#181D27' }}>
            {formatBytes(Object.values(unitStats).reduce((sum: number, s: any) => sum + (s?.uploadedBytes || 0), 0))}
          </Typography>
        </div>

        {/* Download (30j sum) Card */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div style={{ width: 40, height: 40, borderRadius: 8, backgroundColor: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
            </div>
            <Typography sx={{ fontSize: '14px', color: '#717680', fontWeight: 500 }}>Download (30d)</Typography>
          </div>
          <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#181D27' }}>
            {formatBytes(Object.values(unitStats).reduce((sum: number, s: any) => sum + (s?.downloadedBytes || 0), 0))}
          </Typography>
        </div>

        {/* Requests (30D) Card */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div style={{ width: 40, height: 40, borderRadius: 8, backgroundColor: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
            </div>
            <Typography sx={{ fontSize: '14px', color: '#717680', fontWeight: 500 }}>Requests (30D)</Typography>
          </div>
          <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#181D27' }}>
            {Object.values(unitStats).reduce((sum: number, s: any) => sum + (s?.calls || 0), 0).toLocaleString()}
          </Typography>
        </div>
      </div>

      {/* Onboarding Promo Card moved to Home */}
      {false && firstIncomplete && showOnboardingCard && !isOnboardingCompletedInCache && (
        <div
          className="max-w-7xl mx-auto mb-5 bg-[#FDFDFD] border border-[#E9EAEB] rounded-xl flex flex-col sm:flex-row overflow-hidden relative"
          style={{
            minHeight: '154px',
          }}
        >
          {/* Left image section */}
          <div
            className="hidden sm:block"
            style={{ width: 239, height: '100%', position: 'relative', flex: '0 0 239px' }}
          >
            <img
              src={multicloudImg}
              alt="Multicloud"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </div>
          {/* Right content section */}
          <div className="flex-1 flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 gap-3">
            {(() => {
              const nextStepId = firstIncomplete?.id as
                | 'profile'
                | '2fa'
                | 'bucket'
                | 'repo'
                | 'apikey'
                | 'upload'
                | undefined;
              const cta = nextStepId ? getCtaForStep(nextStepId as OnboardingStepId) : null;
              return (
                <div className="flex flex-col w-full sm:w-auto">
                  <div>
                    <div className="text-base font-semibold text-[#181D27]">
                      Finish setting up your account
                    </div>
                    <div className="text-sm text-[#475467] mt-1">
                      Next step:{' '}
                      {nextStepId === 'profile'
                        ? 'Complete your profile'
                        : nextStepId === '2fa'
                        ? 'Enable 2FA and backup codes'
                        : nextStepId === 'bucket'
                        ? 'Create your first bucket'
                        : nextStepId === 'repo'
                        ? 'Create your first repository'
                        : nextStepId === 'apikey'
                        ? 'Generate a Repo API key'
                        : 'Upload your first file'}
                    </div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mt-3">
                      <Button
                        variant="outlined"
                        onClick={() =>
                          window.open('https://docs.flashback.tech/', '_blank', 'noopener,noreferrer')
                        }
                        style={{
                          width: 160,
                          height: 40,
                          opacity: 1,
                          paddingTop: 10,
                          paddingRight: 14,
                          paddingBottom: 10,
                          paddingLeft: 14,
                          gap: 4,
                          borderRadius: 8,
                          borderWidth: 1,
                          borderColor: '#D5D7DA',
                          background: '#FFFFFF',
                          textTransform: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#414651',
                          fontWeight: 600,
                          fontSize: 14,
                          lineHeight: '20px',
                        }}
                      >
                        <span style={{ marginRight: 8 }}>Documentation</span>
                        <img src={docsIcon} alt="docs" style={{ width: 20, height: 20 }} />
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => { cta?.action(); }}
                        style={{
                          width: 134,
                          height: 40,
                          opacity: 1,
                          paddingTop: 10,
                          paddingRight: 14,
                          paddingBottom: 10,
                          paddingLeft: 14,
                          gap: 4,
                          borderRadius: 8,
                          border: '2px solid #617BFF',
                          background: '#617BFF',
                          color: 'white',
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: 14,
                          lineHeight: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {nextStepId === 'bucket' ? (
                          <>
                            <span style={{ fontSize: 14, marginRight: 6 }}>+</span> Add
                          </>
                        ) : (
                          cta?.label ?? 'Learn more'
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })()}
            {/* Right dismiss */}
            <div>
              <button
                onClick={dismissOnboarding}
                title="Dismiss"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  border: '1px solid #E9EAEB',
                  background: 'white',
                  color: '#98A2B3',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'absolute',
                  top: 8,
                  right: 8,
                }}
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto">
        {/* First Row - Storage Activity Card (merged pie chart + bucket list) */}
        <div className="flex flex-col gap-6 mb-8">
          {/* Storage Activity Card - Full Width */}
          <div className="relative" style={{ width: '100%' }}>
            {/* Carte grise de fond avec rayon de bordure */}
            <div
              className="w-full bg-[#F8F9FA] rounded-xl border border-gray-200"
              style={{
                height: '518px',
                position: 'absolute',
                zIndex: 1,
              }}
            ></div>

            {/* Header avec fond légèrement gris */}
            <div
              style={{
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingLeft: '16px',
                paddingRight: '16px',
                position: 'relative',
                zIndex: 2,
              }}
            >
              <Typography
                variant="subtitle1"
                style={{
                  color: '#181D27',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '20px',
                  margin: 0,
                }}
              >
                Storage Activity
              </Typography>
              <Typography
                style={{
                  color: '#535862',
                  fontWeight: 500,
                  fontSize: '13px',
                  lineHeight: '20px',
                }}
              >
                {isLoading ? (
                  'Loading...'
                ) : (
                  `${formatBytesCompact(transferData.reduce((sum, item) => sum + (item.uploadedBytes || 0) + (item.downloadedBytes || 0), 0))}`
                )}
              </Typography>
            </div>

            {/* Carte blanche par-dessus - Split layout: Pie chart left, Bucket list right */}
            <div
              className="w-full bg-white border border-gray-200 shadow-sm flex rounded-xl"
              style={{
                height: '476px',
                marginTop: '2px',
                position: 'relative',
                zIndex: 2,
              }}
            >
              {/* Left side - Pie Chart or empty state */}
              <div
                style={{
                  width: '340px',
                  minWidth: '340px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRight: '1px solid #e5e7eb',
                  padding: '16px',
                }}
              >
                {isLoading ? (
                  <div className="flex flex-col items-center justify-center">
                    <div
                      className="animate-spin rounded-full h-12 w-12 border-b-2 mb-3"
                      style={{ borderColor: '#617BFF' }}
                    ></div>
                    <p className="text-gray-600 text-sm font-medium">Loading data...</p>
                  </div>
                ) : totalTransferValue > 0 ? (
                  <>
                    <div style={{ width: '240px', height: '240px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={transferData}
                            cx="50%"
                            cy="50%"
                            innerRadius={0}
                            outerRadius={110}
                            paddingAngle={0}
                            strokeWidth={0}
                            dataKey="value"
                            nameKey="name"
                            label={false}
                            fill="#8884d8"
                          >
                            {transferData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomTooltip />} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex flex-wrap justify-center gap-2 mt-4">
                      {transferData.map((entry, index) => (
                        <div key={index} className="flex items-center">
                          <div
                            className="w-2.5 h-2.5 mr-1"
                            style={{ backgroundColor: entry.color, display: 'inline-block' }}
                          ></div>
                          <Typography variant="caption" sx={{ fontSize: '11px' }}>{entry.name}</Typography>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                    <div style={{ width: 48, height: 48, borderRadius: 10, border: '1px solid #D5D7DA', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                      <img src={dataIcon} alt="No data" style={{ width: 20, height: 20 }} />
                    </div>
                    <div style={{ color: '#181D27', fontWeight: 600, fontSize: '16px', lineHeight: '24px', textAlign: 'center', marginBottom: 4 }}>
                      No transfer data
                    </div>
                    <div style={{ color: '#535862', fontWeight: 400, fontSize: '14px', lineHeight: '20px', textAlign: 'center' }}>
                      No upload or download activity in the last 30 days.
                    </div>
                  </div>
                )}
              </div>

              {/* Right side - Bucket List */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Header Row */}
                <div
                  style={{
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: 'white',
                    borderBottom: '1px solid #e5e7eb',
                    borderTopRightRadius: '12px',
                  }}
                >
                  <div style={{ flex: 1, paddingLeft: '16px' }}>
                    <Typography
                      variant="caption"
                      style={{
                        color: '#6B7280',
                        fontWeight: 600,
                        fontSize: '12px',
                        lineHeight: '18px',
                        textTransform: 'none',
                        letterSpacing: '0.05em',
                      }}
                    >
                      Name
                    </Typography>
                  </div>
                  <div style={{ width: '70px', textAlign: 'center' }}>
                    <Typography
                      variant="caption"
                      style={{
                        color: '#6B7280',
                        fontWeight: 600,
                        fontSize: '12px',
                        lineHeight: '18px',
                        textTransform: 'none',
                        letterSpacing: '0.05em',
                      }}
                    >
                      Requests
                    </Typography>
                  </div>
                  <div style={{ width: '80px', textAlign: 'center' }}>
                    <Typography
                      variant="caption"
                      style={{
                        color: '#6B7280',
                        fontWeight: 600,
                        fontSize: '12px',
                        lineHeight: '18px',
                        textTransform: 'none',
                        letterSpacing: '0.05em',
                      }}
                    >
                      Upload
                    </Typography>
                  </div>
                  <div style={{ width: '80px', textAlign: 'center' }}>
                    <Typography
                      variant="caption"
                      style={{
                        color: '#6B7280',
                        fontWeight: 600,
                        fontSize: '12px',
                        lineHeight: '18px',
                        textTransform: 'none',
                        letterSpacing: '0.05em',
                      }}
                    >
                      Download
                    </Typography>
                  </div>
                  <div style={{ width: '40px' }}></div>
                </div>

              {/* Bucket List Container - Top 5 only */}
              <div
                style={{
                  flex: 1,
                  overflow: 'hidden',
                }}
              >
                {isLoading ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '200px',
                    }}
                  >
                    <div
                      className="animate-spin rounded-full h-12 w-12 border-b-2 mb-3"
                      style={{ borderColor: '#617BFF' }}
                    ></div>
                    <p className="text-gray-600 text-sm font-medium">Loading buckets...</p>
                  </div>
                ) : buckets.length === 0 ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%',
                      padding: '40px 20px',
                    }}
                  >
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: 10,
                        border: '1px solid #D5D7DA',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 12,
                        background: '#FFFFFF',
                      }}
                    >
                      <img src={thunderIcon} alt="No buckets" style={{ width: 20, height: 20 }} />
                    </div>
                    <div
                      style={{
                        color: '#181D27',
                        fontWeight: 600,
                        fontSize: '16px',
                        lineHeight: '24px',
                        textAlign: 'center',
                        marginBottom: 4,
                      }}
                    >
                      No buckets
                    </div>
                    <div
                      style={{
                        color: '#535862',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        textAlign: 'center',
                      }}
                    >
                      Add your first bucket to see data.
                    </div>
                  </div>
                ) : (
                  [...buckets]
                    .sort((a, b) => {
                      const aTotal = (a.id && unitStats[a.id] ? (unitStats[a.id].downloadedBytes || 0) + (unitStats[a.id].uploadedBytes || 0) : 0);
                      const bTotal = (b.id && unitStats[b.id] ? (unitStats[b.id].downloadedBytes || 0) + (unitStats[b.id].uploadedBytes || 0) : 0);
                      return bTotal - aTotal;
                    })
                    .slice(0, 5)
                    .map((bucket, index) => (
                    <div
                      key={bucket.id || index}
                      style={{
                        height: '76px',
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: '#FFFFFF',
                        borderBottom: '1px solid #F0F0F0',
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        if (bucket.id) {
                          navigate(`/cloud-storage/buckets/${bucket.id}`);
                        }
                      }}
                    >
                      {/* Name + Logo Section */}
                      <div
                        style={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          paddingLeft: '16px',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '12px',
                            flexShrink: 0,
                          }}
                        >
                          {bucket.storageType === ApiTypes.StorageType.S3 && (
                            <img src={awsLogo} alt="AWS S3" style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
                          )}
                          {bucket.storageType === ApiTypes.StorageType.GCS && (
                            <img src={googleLogo} alt="GCS" style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
                          )}
                          {bucket.storageType === ApiTypes.StorageType.BLOB && (
                            <img src={azureLogo} alt="Azure" style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
                          )}
                          {!bucket.storageType && (
                            <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: '#788CFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Typography variant="body2" style={{ color: 'white', fontWeight: 600, fontSize: '16px' }}>
                                {bucket.name.charAt(0).toUpperCase()}
                              </Typography>
                            </div>
                          )}
                        </div>
                        <Typography variant="body2" style={{ color: '#181D27', fontWeight: 500, fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {bucket.name}
                        </Typography>
                      </div>

                      {/* Requests Section */}
                      <div style={{ width: '70px', textAlign: 'center' }}>
                        <Typography variant="body2" style={{ color: '#667085', fontSize: '13px', fontWeight: 400 }}>
                          {bucket.id && unitStats[bucket.id] ? (unitStats[bucket.id].calls || 0).toLocaleString() : '0'}
                        </Typography>
                      </div>

                      {/* Upload Section */}
                      <div style={{ width: '80px', textAlign: 'center' }}>
                        <Typography variant="body2" style={{ color: '#667085', fontSize: '13px', fontWeight: 400 }}>
                          {bucket.id && unitStats[bucket.id] ? formatBytes(unitStats[bucket.id].uploadedBytes) : '0 B'}
                        </Typography>
                      </div>

                      {/* Download Section */}
                      <div style={{ width: '80px', textAlign: 'center' }}>
                        <Typography variant="body2" style={{ color: '#667085', fontSize: '13px', fontWeight: 400 }}>
                          {bucket.id && unitStats[bucket.id] ? formatBytes(unitStats[bucket.id].downloadedBytes) : '0 B'}
                        </Typography>
                      </div>

                      {/* View icon */}
                      <div style={{ width: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#667085" strokeWidth="2">
                          <path d="M9 18l6-6-6-6" />
                        </svg>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Bottom Action Bar */}
              <div
                style={{
                  height: '56px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: '12px',
                  padding: '0 16px',
                  borderTop: '1px solid #E5E7EB',
                  backgroundColor: 'white',
                  borderBottomRightRadius: '12px',
                }}
              >
                <Button
                  variant="contained"
                  size="small"
                  style={{
                    height: '36px',
                    borderRadius: '8px',
                    backgroundColor: '#617BFF',
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '13px',
                    padding: '8px 16px',
                  }}
                  onClick={() => { localStorage.setItem('triggerAddBucket', '1'); navigateToTab(TabIndex.StorageUnits, '/cloud-storage/buckets'); }}
                >
                  Add
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  style={{
                    height: '36px',
                    borderRadius: '8px',
                    border: '1px solid #D0D5DD',
                    backgroundColor: 'white',
                    color: '#344054',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '13px',
                    padding: '8px 16px',
                  }}
                  onClick={() => navigateToTab(TabIndex.StorageUnits, '/cloud-storage/buckets')}
                >
                  View all
                </Button>
              </div>
              </div>{/* End Right side - Bucket List */}
            </div>{/* End White Card */}
          </div>{/* End Storage Activity Card */}
        </div>{/* End First Row */}

        {/* Second Row - Transfer Activity and other cards */}
        <div className="flex flex-col md:flex-row gap-6 mb-8">
          {/* Transfer Activity Card */}
          <div className="relative" style={{ width: '528px', maxWidth: '100%' }}>
            {/* Carte grise de fond avec rayon de bordure */}
            <div
              className="w-full bg-[#F8F9FA] rounded-xl border border-gray-200"
              style={{
                height: '317px',
                position: 'absolute',
                zIndex: 1,
              }}
            ></div>

            {/* Header avec fond légèrement gris */}
            <div
              style={{
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingLeft: '16px',
                paddingRight: '16px',
                position: 'relative',
                zIndex: 2,
              }}
            >
              <Typography
                variant="h6"
                style={{
                  color: '#181D27',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '20px',
                }}
              >
                Data Traffic Rate
              </Typography>
              <Typography
                style={{
                  color: '#535862',
                  fontWeight: 500,
                  fontSize: '13px',
                  lineHeight: '20px',
                }}
              >
                {isLoading ? (
                  <span>Loading...</span>
                ) : (
                  <>
                    Up: {formatBytesCompact(
                      uploadActivityData.reduce(
                        (sum, item) => sum + item.upload * 1024 * 1024 * 1024,
                        0
                      )
                    )} / Down: {formatBytesCompact(
                      downloadActivityData.reduce(
                        (sum, item) => sum + item.download * 1024 * 1024 * 1024,
                        0
                      )
                    )}
                  </>
                )}
              </Typography>
            </div>

            {/* Carte blanche par-dessus */}
            <div
              className="w-full bg-white border border-gray-200 shadow-sm flex flex-col"
              style={{
                height: '275px',
                borderRadius: '12px',
                padding: '16px',
                marginTop: '2px',
                position: 'relative',
                zIndex: 2,
              }}
            >
              <div style={{ height: 210 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={downloadActivityData.map((item, index) => ({
                      ...item,
                      upload: uploadActivityData[index]?.upload || 0,
                    }))}
                    margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
                  >
                    <defs>
                      <linearGradient id="colorUploadOverview" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorDownloadOverview" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={true} />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: '#6B7280' }}
                      stroke="#E5E7EB"
                      tickLine={false}
                      tickFormatter={(value, index) => {
                        // Show 7 labels evenly spaced across 31 data points (0, 5, 10, 15, 20, 25, 30)
                        if (index === 0 || index === 5 || index === 10 || index === 15 || index === 20 || index === 25 || index === 30) {
                          return value;
                        }
                        return '';
                      }}
                      interval={0}
                    />
                    <YAxis
                      width={60}
                      tick={{ fontSize: 11, fill: '#6B7280', style: { whiteSpace: 'nowrap' } }}
                      stroke="#E5E7EB"
                      tickLine={false}
                      tickFormatter={(value) => `${value} GB`}
                      domain={[0, (() => {
                        const maxVal = Math.max(
                          ...downloadActivityData.map(d => d.download),
                          ...uploadActivityData.map(d => d.upload),
                          0
                        );
                        if (maxVal === 0) return 1; // Fictive scale if no data
                        // Round to next "nice" number (tens)
                        const magnitude = Math.pow(10, Math.floor(Math.log10(maxVal)));
                        const normalized = maxVal / magnitude;
                        const rounded = Math.ceil(normalized);
                        return rounded * magnitude;
                      })()]}
                      tickCount={6}
                    />
                    <Tooltip
                      contentStyle={{ backgroundColor: 'white', borderColor: '#ddd', borderRadius: '8px' }}
                      formatter={(value: number, name: string) => [
                        `${value.toFixed(2)} GB`,
                        name === 'download' ? 'Download' : 'Upload',
                      ]}
                      labelFormatter={(label) => `Date: ${label}`}
                    />
                    {visibleTransfers.upload && (
                      <Area
                        type="monotone"
                        dataKey="upload"
                        stroke="#10B981"
                        fill="url(#colorUploadOverview)"
                        strokeWidth={2}
                        name="upload"
                      />
                    )}
                    {visibleTransfers.download && (
                      <Area
                        type="monotone"
                        dataKey="download"
                        stroke="#3B82F6"
                        fill="url(#colorDownloadOverview)"
                        strokeWidth={2}
                        name="download"
                      />
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center gap-4 mt-2">
                <div
                  className="flex items-center cursor-pointer"
                  onClick={() => setVisibleTransfers((prev) => ({ ...prev, upload: !prev.upload }))}
                  style={{ opacity: visibleTransfers.upload ? 1 : 0.5 }}
                >
                  <div className="w-3 h-3 mr-1" style={{ backgroundColor: '#10B981' }}></div>
                  <Typography variant="caption" sx={{ fontSize: '12px', color: '#6B7280' }}>Upload (GB)</Typography>
                </div>
                <div
                  className="flex items-center cursor-pointer"
                  onClick={() =>
                    setVisibleTransfers((prev) => ({ ...prev, download: !prev.download }))
                  }
                  style={{ opacity: visibleTransfers.download ? 1 : 0.5 }}
                >
                  <div className="w-3 h-3 mr-1" style={{ backgroundColor: '#3B82F6' }}></div>
                  <Typography variant="caption" sx={{ fontSize: '12px', color: '#6B7280' }}>Download (GB)</Typography>
                </div>
              </div>
            </div>
          </div>

          {/* Explore Our Guides Card */}
          <div className="relative" style={{ width: '602px', maxWidth: '100%' }}>
            {/* Carte grise de fond avec rayon de bordure */}
            <div
              className="w-full bg-[#F8F9FA] rounded-xl border border-gray-200"
              style={{
                height: '317px',
                position: 'absolute',
                zIndex: 1,
              }}
            ></div>

            {/* Header avec fond légèrement gris */}
            <div
              style={{
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                paddingLeft: '16px',
                paddingRight: '16px',
                position: 'relative',
                zIndex: 2,
              }}
            >
              <Typography
                variant="h6"
                style={{
                  color: '#181D27',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '20px',
                }}
              >
                Explore Our Guides
              </Typography>
            </div>

            {/* Carte blanche par-dessus */}
            <div
              className="w-full bg-white border border-gray-200 shadow-sm flex flex-col"
              style={{
                height: '275px',
                borderRadius: '12px',
                padding: '16px',
                marginTop: '2px',
                position: 'relative',
                zIndex: 2,
              }}
            >
              <div
                style={{
                  maxHeight: 240,
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                <style>{`
                  div::-webkit-scrollbar {
                    display: none;
                  }
                `}</style>
                <div className="flex space-x-4" style={{ paddingBottom: '4px' }}>
                  {/* Guide 1: Create a Bucket */}
                  <div
                    style={{
                      width: 216,
                      height: 235,
                      minWidth: '216px',
                      borderRadius: '12px',
                      borderWidth: '1px',
                      borderColor: '#e5e7eb',
                      borderStyle: 'solid',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      padding: '20px',
                      backgroundColor: 'white',
                    }}
                  >
                    <div>
                      <Typography
                        style={{
                          fontWeight: 600,
                          fontSize: '16px',
                          color: '#181D27',
                          lineHeight: '22px',
                          marginBottom: '8px',
                        }}
                      >
                        Create a Bucket
                      </Typography>
                      <Typography
                        style={{
                          fontSize: '13px',
                          color: '#6B7280',
                          lineHeight: '18px',
                        }}
                      >
                        Learn how to create and configure cloud storage buckets
                      </Typography>
                    </div>
                    <div
                      onClick={() =>
                        window.open(
                          'https://docs.flashback.tech/guides/setup-the-cloud-and-ai-gateway/start-with-cloud-storage/create-a-bucket',
                          '_blank'
                        )
                      }
                      style={{
                        color: '#4766FF',
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: '20px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      View guide
                      <span style={{ fontSize: '16px' }}>→</span>
                    </div>
                  </div>

                  {/* Guide 2: Cloud Storage API Reference */}
                  <div
                    style={{
                      width: 216,
                      height: 235,
                      minWidth: '216px',
                      borderRadius: '12px',
                      borderWidth: '1px',
                      borderColor: '#e5e7eb',
                      borderStyle: 'solid',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      padding: '20px',
                      backgroundColor: 'white',
                    }}
                  >
                    <div>
                      <Typography
                        style={{
                          fontWeight: 600,
                          fontSize: '16px',
                          color: '#181D27',
                          lineHeight: '22px',
                          marginBottom: '8px',
                        }}
                      >
                        Cloud Storage API Reference
                      </Typography>
                      <Typography
                        style={{
                          fontSize: '13px',
                          color: '#6B7280',
                          lineHeight: '18px',
                        }}
                      >
                        Explore the complete Cloud Storage API documentation
                      </Typography>
                    </div>
                    <div
                      onClick={() =>
                        window.open(
                          'https://docs.flashback.tech/support-reference/platform-api-reference/storage-apis',
                          '_blank'
                        )
                      }
                      style={{
                        color: '#4766FF',
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: '20px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      View docs
                      <span style={{ fontSize: '16px' }}>→</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Third Row */}
        {/* <div>
          <div className="w-full p-2 rounded-lg bg-white border border-gray-200 shadow-sm">
            <div className="flex justify-between items-center mb-2">
              <Typography
                variant="subtitle1"
                style={{
                  color: '#788CFF',
                  opacity: 0.9,
                  fontWeight: 500,
                  borderLeft: '3px solid #788CFF',
                  paddingLeft: '8px',
                }}
              >
                Recent activity
              </Typography>
              <Button size="small" variant="text" style={{ color: '#EBBDA3' }}>
                Filter
              </Button>
            </div>
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                </tr>
              </thead>
              <tbody>
                {recentActivity.map((activity, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="py-2 px-3 text-sm font-medium">{activity.action}</td>
                    <td className="py-2 px-3 text-sm text-gray-500">{activity.timestamp}</td>
                    <td className="py-2 px-3 text-sm">{activity.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-center mt-2 text-xs text-gray-500">
              Table breakdown (activity, timestamp, description etc)
            </div>
          </div>
        */}
      </div>
      </div>
    </div>
  );
};

export default OverviewMockup;
